<template>
  <MultiSelectFilter
    :show-menu="showMenu"
    :title="tacticContextTitle"
    :subtitle="tacticContextsText"
    :numberOfSelectedValues="selectedValues"
    @handleMenu="handleOpenMenu"
    @clearFilters="onClearFilters"
  >
    <BbPopover
      :model-value="showMenu"
      :hideCloseButton="true"
      location="bottom"
      contentClass="authored-filters-popup"
      width="92%"
      @update:modelValue="handleOpenMenu(false)"
    >
      <template #popover>
        <div class="popover-container">
          <div class="context-types-list">
            <TacticsContextsRegionFilter
              :isOpened="showMenu"
              :selectedRegion="selectedRegion"
              @change-region="resetTactics"
            />
            <TacticContextTypeCard
              v-for="contextTacticType in tacticContextTypes"
              :key="contextTacticType.id"
              :nrOfTactics="tacticContexts[contextTacticType.id].length"
              :context-type="contextTacticType"
              :selected-context-type-id="selectedTacticContextType"
              @set-selected-type="handleSelectedContextType"
            />
          </div>
          <div class="divider" />
          <div class="context-tactics-list">
            <FilterLabel
              v-for="contextTactic in tacticContexts[selectedTacticContextType]"
              :key="contextTactic.id"
              :label="contextTactic.name"
              :is-selected="getFilterTacticValue() === contextTactic.id"
              @set-filter-value="handleFilter(contextTactic)"
            />
          </div>
        </div>
      </template>
    </BbPopover>
  </MultiSelectFilter>
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { mapTacticToTacticType } from '~/utils/editor-utils';
import useAuthoring from '~/composables/useAuthoring';
import TacticContextTypeCard from '~/components/conversation/authoring/tacticsContexts/TacticContextTypeCard';
import TacticsContextsRegionFilter from '~/components/conversation/authoring/tacticsContexts/TacticsContextsRegionFilter.vue';
import MultiSelectFilter from '~/components/conversation/authoring/common/MultiSelectFilter.vue';
import FilterLabel from '~/components/filters/FilterLabel.vue';

const prepareTacticContexts = (defaultsTactics, currentTacticTypes) => {
  const tacticsToTacticType = mapTacticToTacticType(currentTacticTypes);
  return Object.entries(tacticsToTacticType).reduce(
    (acc, [key, value]) => {
      acc[key] = acc[key] ? [...acc[key], ...value] : value;
      return acc;
    },
    { ...defaultsTactics },
  );
};

export default {
  components: { MultiSelectFilter, TacticContextTypeCard, FilterLabel, TacticsContextsRegionFilter },
  props: {
    supportedLanguages: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const { t } = useI18n();
    const showMenu = ref(false);
    const {
      tacticContextFilters,
      setTacticContextFilters,
      resetTacticContextFilters,
      getTacticTypeAndTacticsByRegion,
      expandedFilters,
      setExpandedFilters,
    } = useAuthoring();
    // make sure only tactics with type that still exist are counted
    const nrOfContexts = computed(
      () => Object.values(tacticContextFilters.value).filter((item) => item.regionDescriptionName).length,
    );
    const tacticContextsText = computed(() =>
      nrOfContexts.value
        ? `${nrOfContexts.value} ${nrOfContexts.value > 1 ? t('author.filters.styles') : t('author.filters.style')}`
        : t('author.filters.pickFiltering'),
    );
    const tacticContextTitle = `${t('author.filters.context')} (${t('common.optional')})`;

    const tacticContextTypes = ref([]);

    const selectedTacticContextType = ref('');

    const selectedRegion = ref({ id: 'global', title: 'None Selected' });

    const defaultTacticContexts = ref({});

    const tacticContexts = ref({});

    const handleOpenMenu = async (value) => {
      showMenu.value = value;
      if (value) {
        setExpandedFilters(value);
      }

      // when we open the menu for the first time we need to fetch the global tactic contexts
      // selectedRegion is this case will be 'global'
      // if tacticContextTypes have already been fetched (menu opened second time) we do not need to fetch them again
      // preserve the state before closing it
      if (value && !tacticContextTypes.value.length) {
        tacticContextTypes.value = await getTacticTypeAndTacticsByRegion(selectedRegion.value.id || selectedRegion);

        if (tacticContextTypes.value.length) {
          defaultTacticContexts.value = mapTacticToTacticType(tacticContextTypes.value);
          selectedTacticContextType.value = tacticContextTypes.value[0].id;
          tacticContexts.value = defaultTacticContexts.value;
        }
      }
    };

    const onClearFilters = () => {
      resetTacticContextFilters({});
    };

    const handleFilter = (tacticContext) => {
      setTacticContextFilters({ contextTypeId: selectedTacticContextType.value, tacticContext });
    };

    const handleSelectedContextType = (id) => {
      selectedTacticContextType.value = id;
    };

    const getFilterTacticValue = () => tacticContextFilters.value[selectedTacticContextType.value]?.id;

    const resetTactics = async (regionId) => {
      // this is called when user changes region
      selectedRegion.value = regionId;

      if (regionId !== 'global') {
        const tactics = await getTacticTypeAndTacticsByRegion(regionId);
        tacticContexts.value = prepareTacticContexts(defaultTacticContexts.value, tactics);
      } else {
        tacticContexts.value = defaultTacticContexts.value;
      }
    };

    return {
      defaultTacticContexts,
      showMenu,
      t,
      getFilterTacticValue,
      handleFilter,
      handleOpenMenu,
      handleSelectedContextType,
      resetTactics,
      tacticContexts,
      tacticContextTypes,
      selectedTacticContextType,
      onClearFilters,
      tacticContextFilters,
      tacticContextsText,
      tacticContextTitle,
      selectedRegion,
      expandedFilters,
      selectedValues: nrOfContexts,
    };
  },
};
</script>

<style scoped lang="scss">
.popover-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-6;

  .context-types-list {
    display: flex;
    flex-direction: column;
    margin: 25px 0 20px 20px;
    flex: 1;
  }

  .context-tactics-list {
    display: flex;
    height: fit-content;
    gap: $bb-spacing-4;
    flex-wrap: wrap;
    flex-direction: row;
    margin: 30px 0 6px 0;
    flex: 2;
  }

  .divider {
    width: 2px;
    height: inherit;
    background: $bb-neutral-5;
  }
}
</style>
